// Capabilities per connector. Each one becomes a node:
//   kind "action" -> palette node (connector_action: HTTP via the connector)
//   kind "event"  -> trigger ("Ao ...", app dispara webhook -> inicia o fluxo)
//
// Os 10 principais têm method/path/body REAIS (conforme a doc pública de cada
// API) — mas ainda precisam de teste com credencial real e, em alguns, de IDs
// da conta (pipeline, etapa, tags). O `path`/`body` são editáveis no nó.
const A = (key, label, method = "POST", path = "", body = "") => ({
  key,
  label,
  kind: "action",
  method,
  path,
  body,
});
// out (opcional): schema de SAÍDA do evento — os campos que chegam no
// steps.trigger.event.body quando o gatilho dispara. Serve para o nó/copiloto
// conhecerem o formato do JSON SEM precisar explicar (mapeamento de variáveis).
//   { fields: [{ path, desc }], sample: {...} }
// setup (opcional): passo a passo de como ligar o evento DO LADO DA FERRAMENTA,
// quando ela não dispara sozinha (ex.: exige montar uma campanha/automação que
// chame a nossa URL). Vira um "?" no nó do canvas que abre o guia.
//   { title, intro?, steps: [string], note? }
const E = (key, label, out = null, setup = null) => ({
  key,
  label,
  kind: "event",
  ...(out ? { out } : {}),
  ...(setup ? { setup } : {}),
});

// Schema do ticket do Movidesk (webhook de gatilho + GET /tickets compartilham o
// mesmo objeto). Paths relativos a steps.trigger.event.body.
const MOVIDESK_TICKET = {
  fields: [
    { path: "id", desc: "id interno do ticket" },
    { path: "protocol", desc: "protocolo (número visível do chamado)" },
    { path: "subject", desc: "assunto do ticket" },
    { path: "baseStatus", desc: "status base: New | InAttendance | Stopped | Canceled | Closed" },
    { path: "status", desc: "status atual (texto configurado na conta, ex. 'Fechado')" },
    { path: "category", desc: "categoria do ticket" },
    { path: "urgency", desc: "urgência" },
    { path: "lastUpdate", desc: "data/hora da última atualização (ISO 8601)" },
    { path: "clients[0].id", desc: "id do cliente do ticket" },
    { path: "clients[0].businessName", desc: "nome do cliente / empresa" },
    { path: "clients[0].email", desc: "e-mail do cliente" },
    { path: "clients[0].phone", desc: "telefone do cliente (use como destino da chamada NPS)" },
    { path: "owner.businessName", desc: "agente responsável pelo atendimento" },
  ],
  sample: {
    id: 12345,
    protocol: "2026000123",
    subject: "Problema no login",
    baseStatus: "Closed",
    status: "Fechado",
    category: "Suporte",
    urgency: "Normal",
    lastUpdate: "2026-06-23T14:05:00.000Z",
    clients: [{ id: "987", businessName: "ACME Ltda", email: "contato@acme.com", phone: "5531999990000" }],
    owner: { businessName: "Ana Suporte" },
  },
};

// Schema da mensagem de WhatsApp RECEBIDA (gatilho "Ao receber mensagem").
// Paths relativos a steps.trigger.event.body. ATENÇÃO: para RESPONDER, o destino
// é o `from` (quem te mandou) — NÃO invente `sender`/`telefone`.
const WHATSAPP_INBOUND = {
  fields: [
    { path: "from", desc: "número de quem ENVIOU (DDI+DDD+número). USE COMO DESTINO ao responder." },
    { path: "text", desc: "texto da mensagem recebida (quando type=text)" },
    { path: "name", desc: "nome do contato (perfil do WhatsApp)" },
    { path: "type", desc: "tipo da mensagem: text | image | audio | document | ..." },
    { path: "messageId", desc: "id único da mensagem recebida (wamid)" },
    { path: "contact.wa_id", desc: "identificador WhatsApp do contato" },
    { path: "metadata.phone_number_id", desc: "id do número da conta que recebeu" },
    { path: "timestamp", desc: "horário do envio (epoch, segundos)" },
  ],
  sample: {
    from: "5519999990000",
    name: "Maria Silva",
    text: "oi",
    type: "text",
    messageId: "wamid.HBgNNTUxOTk5OTk5MDAwMBUC",
    timestamp: "1782216904",
    contact: { profile: { name: "Maria Silva" }, wa_id: "5519999990000" },
    metadata: { display_phone_number: "551930950000", phone_number_id: "1203274539533684" },
  },
};

/* ──────────────────────────── Attio (CRM) ────────────────────────────
   Gerado das specs OpenAPI oficiais do Attio:
     https://api.attio.com/openapi/api        (97 operações)
     https://api.attio.com/openapi/webhooks   (OpenAPI 3.1, chave `webhooks`)
   São os 34 eventos que o Attio publica — a lista completa, sem recorte — e as
   97 operações da API v2. Base fixa https://api.attio.com, auth Bearer.
   NADA AQUI FOI EXERCITADO CONTRA A API REAL: a spec diz o contrato, não prova
   a chamada.                                                                */

// activity.created, activity.updated, activity.deleted
const ATTIO_EV_ATIVIDADE = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.activity_id", desc: "id da atividade criada" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "activity.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "activity_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// activity-attribute.created, activity-attribute.updated
const ATTIO_EV_ATIVIDADE_ATRIBUTO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.activity_id", desc: "id da atividade onde o atributo foi criado" },
    { path: "events[0].id.attribute_id", desc: "id do atributo de atividade criado" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "activity-attribute.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "activity_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "attribute_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// activity-record.created, activity-record.deleted
const ATTIO_EV_ATIVIDADE_REGISTRO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.activity_id", desc: "id da atividade onde o registro foi criado" },
    { path: "events[0].id.record_id", desc: "id do registro de atividade criado" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "activity-record.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "activity_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// call-recording.created
const ATTIO_EV_MEETING_ID_GRAVACAO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.meeting_id", desc: "id da reunião da gravação" },
    { path: "events[0].id.call_recording_id", desc: "id da gravação de chamada criada" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "call-recording.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "meeting_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "call_recording_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// comment.created, comment.resolved, comment.unresolved, comment.deleted
const ATTIO_EV_COMENTARIO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.comment_id", desc: "id do comentário criado" },
    { path: "events[0].thread_id", desc: "id da conversa do comentário" },
    { path: "events[0].object_id", desc: "id do objeto a que o comentário se refere" },
    { path: "events[0].record_id", desc: "id do registro a que o comentário se refere" },
    { path: "events[0].list_id", desc: "id da lista, quando o comentário é de um item de lista; senão null" },
    { path: "events[0].entry_id", desc: "id do item de lista, quando o comentário é de um item; senão null" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "comment.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "comment_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "thread_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "list_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "entry_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// list.created, list.updated, list.deleted
const ATTIO_EV_LISTA = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.list_id", desc: "id da lista criada" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "list.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "list_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// list-attribute.created, list-attribute.updated
const ATTIO_EV_LISTA_ATRIBUTO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.list_id", desc: "id da lista onde o atributo foi criado" },
    { path: "events[0].id.attribute_id", desc: "id do atributo de lista criado" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "list-attribute.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "list_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "attribute_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// list-entry.created, list-entry.deleted
const ATTIO_EV_LISTA_ITEM = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.list_id", desc: "id da lista onde o item foi criado" },
    { path: "events[0].id.entry_id", desc: "id do item de lista criado" },
    { path: "events[0].parent_object_id", desc: "id do objeto de origem do item criado" },
    { path: "events[0].parent_record_id", desc: "id do registro de origem do item criado" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "list-entry.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "list_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "entry_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "parent_object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "parent_record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// list-entry.updated
const ATTIO_EV_LISTA_ITEM_ATRIBUTO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.list_id", desc: "id da lista onde o item foi atualizado" },
    { path: "events[0].id.entry_id", desc: "id do item de lista atualizado" },
    { path: "events[0].id.attribute_id", desc: "id do atributo cujo valor mudou no item" },
    { path: "events[0].parent_object_id", desc: "id do objeto de origem do item atualizado" },
    { path: "events[0].parent_record_id", desc: "id do registro de origem do item atualizado" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "list-entry.updated",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "list_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "entry_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "attribute_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "parent_object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "parent_record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// object-attribute.created, object-attribute.updated
const ATTIO_EV_OBJECT_ID_ATRIBUTO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.object_id", desc: "id do objeto onde o atributo foi criado" },
    { path: "events[0].id.attribute_id", desc: "id do atributo de objeto criado" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "object-attribute.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "attribute_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// note.created, note-content.updated, note.updated, note.deleted
const ATTIO_EV_NOTA = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.note_id", desc: "id da nota criada" },
    { path: "events[0].parent_object_id", desc: "id do objeto a que a nota criada pertence" },
    { path: "events[0].parent_record_id", desc: "id do registro a que a nota criada pertence" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "note.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "note_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "parent_object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "parent_record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// record.created, record.deleted
const ATTIO_EV_OBJECT_ID_REGISTRO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.object_id", desc: "id do objeto do registro (people, companies, deals...)" },
    { path: "events[0].id.record_id", desc: "id do registro criado" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "record.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// record.merged
const ATTIO_EV_OBJECT_ID_REGISTRO_2 = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.object_id", desc: "id do objeto dos registros mesclados (só é possível mesclar dentro do mesmo objeto)" },
    { path: "events[0].id.record_id", desc: "id do registro resultante da mesclagem (igual nos dois eventos da mesma mesclagem)" },
    { path: "events[0].duplicate_object_id", desc: "id do objeto do registro original (sempre igual ao id.object_id)" },
    { path: "events[0].duplicate_record_id", desc: "id de um dos dois registros originais da mesclagem (o Attio dispara um evento por registro)" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "record.merged",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "duplicate_object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "duplicate_record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// record.updated
const ATTIO_EV_OBJECT_ID_REGISTRO_ATRIBUTO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.object_id", desc: "id do objeto do registro (people, companies, deals...)" },
    { path: "events[0].id.record_id", desc: "id do registro cujo atributo mudou" },
    { path: "events[0].id.attribute_id", desc: "id do atributo que mudou no registro" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "record.updated",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "object_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "record_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "attribute_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// task.created, task.updated, task.deleted
const ATTIO_EV_TAREFA = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.task_id", desc: "id da tarefa criada" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "task.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "task_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// workspace-member.created
const ATTIO_EV_MEMBRO = {
  fields: [
    { path: "webhook_id", desc: "id do webhook que entregou este evento" },
    { path: "events[0].event_type", desc: "tipo do evento que disparou" },
    { path: "events[0].id.workspace_id", desc: "id do workspace no Attio" },
    { path: "events[0].id.workspace_member_id", desc: "id do membro adicionado ao workspace" },
    { path: "events[0].actor.id", desc: "id de quem fez a ação" },
    { path: "events[0].actor.type", desc: "quem fez a ação: workspace-member | system | api-token | app" },
  ],
  sample: {
    "webhook_id": "23e42eaf-323a-41da-b5bb-fd67eebda553",
    "events": [
      {
        "event_type": "workspace-member.created",
        "id": {
          "workspace_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b",
          "workspace_member_id": "1b2c3d4e-5f60-4a71-8b92-0c1d2e3f4a5b"
        },
        "actor": {
          "id": "50cf242c-7fa3-4cad-87d0-75b1af71c57b",
          "type": "workspace-member"
        }
      }
    ]
  },
};

// Como ligar os gatilhos do Attio. O Attio NÃO dispara nada sozinho: é preciso
// criar um webhook apontando para a URL deste fluxo — pela tela do Attio ou pela
// própria ação "Criar webhook" deste conector.
const ATTIO_SETUP = {
  title: "Ligar o gatilho no Attio",
  intro:
    "O Attio entrega os eventos num webhook que você cria apontando para a URL deste fluxo. " +
    "Um webhook pode assinar vários tipos de evento — mas cada fluxo recebe o que você assinar, " +
    "então assine aqui só o evento deste gatilho.",
  steps: [
    "Copie a URL de webhook deste fluxo (fica no próprio nó do gatilho).",
    "No Attio, abra Workspace settings → Developers → Webhooks → Create webhook.",
    "Em Target URL, cole a URL do fluxo. Precisa ser https.",
    "Em Subscriptions, escolha o tipo de evento correspondente a este gatilho (o nome técnico está na descrição do nó).",
    "Opcional, mas recomendado: em cada subscription dê um filtro (ex.: só um objeto, só uma lista) — sem filtro o fluxo roda a cada mudança do workspace.",
    "Salve. O Attio mostra UMA ÚNICA VEZ o campo `secret` — guarde-o se for validar a assinatura.",
  ],
  note:
    "O Attio desiste da entrega em 5 SEGUNDOS e retenta até 10 vezes ao longo de ~3 dias quando a resposta não é 2xx. " +
    "Como o fluxo responde só depois de rodar, um fluxo lento é reentregue e executa de novo: ponha um nó " +
    '"Resposta HTTP" cedo, ou mantenha o trecho síncrono curto. ' +
    "Cada entrega traz um ARRAY `events` — pode vir mais de um evento na mesma chamada, e os campos abaixo são os do primeiro.",
};

export const CONNECTOR_CAPS = {
  // ── WhatsApp ──────────────────────────────────────────────────────────
  gupshup: [
    A(
      "enviar_whatsapp",
      "Enviar WhatsApp",
      "POST",
      "/msg",
      '{\n  "destination": "{{steps.trigger.event.body.telefone}}",\n  "message": "{\\"type\\":\\"text\\",\\"text\\":\\"{{transcript}}\\"}"\n}'
    ),
    A(
      "enviar_template",
      "Enviar template",
      "POST",
      "/template/msg",
      '{\n  "destination": "{{steps.trigger.event.body.telefone}}",\n  "template": "{\\"id\\":\\"SEU_TEMPLATE_ID\\",\\"params\\":[]}"\n}'
    ),
    E("ao_receber_mensagem", "Ao receber mensagem", WHATSAPP_INBOUND),
  ],
  // WhatsApp NÃO oficial via QR Code (self-hosted / Baileys). O envio compila
  // para o verbo `whatsapp_qr_send` (override em verbs.js) — não é HTTP como os
  // demais connector_action. O inbound chega pelo socket local (nativo).
  whatsapp_qr: [
    A("enviar_mensagem", "Enviar mensagem WhatsApp"),
    A("consultar_perfil", "Extrair nome e foto"),
    E("ao_receber_mensagem", "Ao receber mensagem", WHATSAPP_INBOUND),
  ],
  // ── Mensageria ────────────────────────────────────────────────────────
  // Telegram Bot API. O token vai no CAMINHO (api.telegram.org/bot<TOKEN>),
  // montado pelo executor — por isso path aqui começa em /sendMessage.
  // `chat_id` aceita o id numérico do chat/grupo ou @canal.
  telegram: [
    A(
      "enviar_mensagem",
      "Enviar mensagem",
      "POST",
      "/sendMessage",
      '{\n  "chat_id": "{{steps.trigger.event.body.chat_id}}",\n  "text": "{{transcript}}"\n}'
    ),
    A(
      "enviar_foto",
      "Enviar foto",
      "POST",
      "/sendPhoto",
      '{\n  "chat_id": "{{steps.trigger.event.body.chat_id}}",\n  "photo": "https://exemplo.com/imagem.jpg",\n  "caption": ""\n}'
    ),
    A(
      "enviar_documento",
      "Enviar documento",
      "POST",
      "/sendDocument",
      '{\n  "chat_id": "{{steps.trigger.event.body.chat_id}}",\n  "document": "{{steps.trigger.call.recordingPublicUrl}}",\n  "caption": "Gravação da chamada"\n}'
    ),
  ],
  // ── CRM ───────────────────────────────────────────────────────────────
  rdstation: [
    A("criar_negocio", "Criar negócio", "POST", "/deals", '{\n  "deal": { "name": "{{steps.trigger.event.body.nome}}" }\n}'),
    A("buscar_contato", "Buscar contato por telefone", "GET", "/contacts?phone={{steps.trigger.event.body.telefone}}"),
    A("registrar_tarefa", "Registrar tarefa/atividade", "POST", "/tasks", '{\n  "task": { "subject": "Ligação", "notes": "{{transcript}}" }\n}'),
    E("ao_criar_lead", "Ao criar lead"),
    E("ao_mudar_etapa", "Ao mudar etapa do funil"),
  ],
  pipedrive: [
    A("criar_negocio", "Criar negócio", "POST", "/deals", '{\n  "title": "{{steps.trigger.event.body.titulo}}"\n}'),
    A("mover_etapa", "Mover etapa do negócio", "PUT", "/deals/{{steps.trigger.event.body.deal_id}}", '{\n  "stage_id": 1\n}'),
    A("registrar_atividade", "Registrar atividade da ligação", "POST", "/activities", '{\n  "subject": "Ligação",\n  "type": "call",\n  "note": "{{transcript}}",\n  "done": 1\n}'),
    A("buscar_contato", "Buscar contato por telefone", "GET", "/persons/search?term={{steps.trigger.event.body.telefone}}&fields=phone"),
    E("ao_criar_negocio", "Ao criar negócio"),
    E("ao_mudar_etapa", "Ao mudar etapa"),
  ],
  bitrix: [
    A("criar_lead", "Criar lead", "POST", "/crm.lead.add.json", '{\n  "fields": {\n    "TITLE": "{{steps.trigger.event.body.titulo}}",\n    "PHONE": [{ "VALUE": "{{steps.trigger.event.body.telefone}}", "VALUE_TYPE": "WORK" }]\n  }\n}'),
    A("registrar_atividade", "Registrar atividade", "POST", "/crm.activity.add.json", '{\n  "fields": { "SUBJECT": "Ligação", "DESCRIPTION": "{{transcript}}" }\n}'),
    A("buscar_contato", "Buscar contato por telefone", "POST", "/crm.contact.list.json", '{\n  "filter": { "PHONE": "{{steps.trigger.event.body.telefone}}" }\n}'),
    E("ao_criar_lead", "Ao criar/atualizar lead"),
  ],
  hubspot: [
    A("upsert_contato", "Criar/atualizar contato", "POST", "/crm/v3/objects/contacts", '{\n  "properties": {\n    "phone": "{{steps.trigger.event.body.telefone}}",\n    "firstname": "{{steps.trigger.event.body.nome}}"\n  }\n}'),
    A("criar_negocio", "Criar negócio", "POST", "/crm/v3/objects/deals", '{\n  "properties": { "dealname": "{{steps.trigger.event.body.titulo}}" }\n}'),
    A("registrar_chamada", "Registrar chamada", "POST", "/crm/v3/objects/calls", '{\n  "properties": {\n    "hs_call_body": "{{transcript}}",\n    "hs_call_direction": "OUTBOUND"\n  }\n}'),
    E("ao_criar_contato", "Ao criar contato"),
  ],

  // ── ERP ───────────────────────────────────────────────────────────────
  bling: [
    A("consultar_pedido", "Consultar pedido", "GET", "/pedidos/vendas/{{steps.trigger.event.body.pedido_id}}"),
    A("consultar_produto", "Consultar produto/preço", "GET", "/produtos/{{steps.trigger.event.body.produto_id}}"),
    A("buscar_contato", "Buscar contato", "GET", "/contatos?pesquisa={{steps.trigger.event.body.telefone}}"),
    E("ao_receber_pedido", "Ao receber pedido"),
    E("ao_mudar_status_pedido", "Ao mudar status do pedido"),
  ],

  // ── Cobrança ──────────────────────────────────────────────────────────
  asaas: [
    A("consultar_cobranca", "Consultar cobrança do cliente", "GET", "/payments?customer={{steps.trigger.event.body.cliente_id}}"),
    A("gerar_2via", "Gerar 2ª via (linha digitável)", "GET", "/payments/{{steps.trigger.event.body.cobranca_id}}/identificationField"),
    A("consultar_pagamento", "Consultar status de pagamento", "GET", "/payments/{{steps.trigger.event.body.cobranca_id}}"),
    E("ao_vencer_cobranca", "Ao vencer cobrança"),
    E("ao_confirmar_pagamento", "Ao confirmar pagamento"),
  ],

  // ── Helpdesk ──────────────────────────────────────────────────────────
  // Movidesk usa formulários amigáveis (`form`): o usuário só vê os campos que
  // importam (ex.: "ID do ticket"); método/caminho/corpo são montados por baixo.
  movidesk: [
    {
      key: "abrir_ticket", label: "Abrir ticket", kind: "action", method: "POST", path: "/tickets",
      form: [
        { name: "assunto", label: "Assunto", type: "text", default: "{{steps.trigger.event.body.assunto}}", helperText: "Assunto do chamado. Aceita variável." },
        { name: "clienteId", label: "ID do cliente", type: "text", default: "{{steps.trigger.event.body.cliente_id}}", helperText: "Cliente do ticket (id no Movidesk)." },
        { name: "descricao", label: "Descrição", type: "textarea", default: "{{transcript}}", helperText: "Primeira ação do ticket (texto)." },
      ],
      body: d => {
        // nó legado (sem os campos do form) guardava o body como string JSON
        if (!d.assunto && !d.clienteId && !d.descricao && d.body) return d.body;
        return {
          type: 2,
          subject: d.assunto || "",
          category: "Atendimento",
          clients: d.clienteId ? [{ id: String(d.clienteId) }] : [],
          actions: [{ type: 2, description: d.descricao || "{{transcript}}" }],
        };
      },
    },
    {
      // 1 ticket específico: ?id=<numero> (não precisa $select). Campo do webhook: `id`.
      key: "buscar_ticket", label: "Buscar ticket por id", kind: "action", method: "GET",
      form: [
        { name: "ticketId", label: "ID do ticket", type: "text", default: "{{steps.trigger.event.body.id}}", placeholder: "{{steps.trigger.event.body.id}} ou 700001", helperText: "Número do ticket a buscar. Aceita variável." },
      ],
      path: d => (d.ticketId != null ? `/tickets?id=${String(d.ticketId).trim()}` : (d.path || "/tickets?id={{steps.trigger.event.body.id}}")),
    },
    {
      // Listar/filtrar exige $select (senão 400); $filter/$top recomendados.
      key: "listar_tickets", label: "Listar tickets (filtro)", kind: "action", method: "GET",
      form: [
        { name: "baseStatus", label: "Status", type: "select", default: "New", options: [
          { value: "New", label: "Novos" }, { value: "InAttendance", label: "Em atendimento" },
          { value: "Stopped", label: "Parados" }, { value: "Closed", label: "Fechados" }, { value: "Canceled", label: "Cancelados" },
        ], helperText: "Filtra pelos tickets neste status." },
        { name: "top", label: "Máximo de resultados", type: "number", default: 20, helperText: "Quantos tickets trazer (até 100)." },
      ],
      path: d => `/tickets?$select=id,protocol,subject,status,baseStatus,owner,clients&$filter=baseStatus eq '${d.baseStatus || "New"}'&$top=${d.top || 20}`,
    },
    {
      key: "atualizar_ticket", label: "Atualizar ticket", kind: "action", method: "PATCH",
      form: [
        { name: "ticketId", label: "ID do ticket", type: "text", default: "{{steps.trigger.event.body.id}}", helperText: "Ticket a atualizar. Aceita variável." },
        { name: "status", label: "Novo status", type: "text", default: "Em atendimento", placeholder: "Ex.: Em atendimento, Fechado", helperText: "Status exatamente como configurado na conta Movidesk." },
      ],
      path: d => (d.ticketId != null ? `/tickets?id=${String(d.ticketId).trim()}` : (d.path || "/tickets?id={{steps.trigger.event.body.id}}")),
      body: d => ({ status: d.status || "Em atendimento" }),
    },
    E("ao_abrir_ticket", "Ao abrir ticket", MOVIDESK_TICKET),
    E("ao_mudar_status_ticket", "Ao mudar status do ticket", MOVIDESK_TICKET),
  ],

  // ── Marketing ─────────────────────────────────────────────────────────
  activecampaign: [
    A("upsert_contato", "Criar/atualizar contato", "POST", "/api/3/contact/sync", '{\n  "contact": {\n    "email": "{{steps.trigger.event.body.email}}",\n    "phone": "{{steps.trigger.event.body.telefone}}"\n  }\n}'),
    A("aplicar_tag", "Aplicar tag", "POST", "/api/3/contactTags", '{\n  "contactTag": { "contact": "{{steps.trigger.event.body.contato_id}}", "tag": "1" }\n}'),
    A("add_automacao", "Adicionar a automação", "POST", "/api/3/contactAutomations", '{\n  "contactAutomation": { "contact": "{{steps.trigger.event.body.contato_id}}", "automation": "1" }\n}'),
    E("ao_disparar_automacao", "Ao disparar automação"),
  ],
  brevo: [
    A("enviar_email", "Enviar e-mail", "POST", "/smtp/email", '{\n  "to": [{ "email": "{{steps.trigger.event.body.email}}" }],\n  "sender": { "name": "Numera", "email": "no-reply@seu-dominio.com" },\n  "subject": "{{steps.trigger.event.body.assunto}}",\n  "htmlContent": "<p>{{transcript}}</p>"\n}'),
    A("enviar_sms", "Enviar SMS", "POST", "/transactionalSMS/sms", '{\n  "sender": "Numera",\n  "recipient": "{{steps.trigger.event.body.telefone}}",\n  "content": "{{transcript}}"\n}'),
    A("add_lista", "Adicionar contato a lista", "POST", "/contacts", '{\n  "email": "{{steps.trigger.event.body.email}}",\n  "listIds": [1]\n}'),
  ],

  // ── Tier-2 (apenas rótulo/endpoint placeholder por enquanto) ──────────
  ploomes: [
    A("criar_negocio", "Criar negócio", "POST", "/Deals"),
    A("buscar_cliente", "Buscar cliente por telefone", "GET", "/Contacts"),
    E("ao_criar_negocio", "Ao criar negócio"),
  ],
  agendor: [
    A("criar_negocio", "Criar negócio"),
    A("registrar_atividade", "Registrar atividade"),
    E("ao_mudar_etapa", "Ao mudar etapa"),
  ],
  tiny: [
    A("consultar_pedido", "Consultar pedido", "POST", "/pedido.obter.php"),
    A("consultar_produto", "Consultar produto/estoque", "POST", "/produto.obter.estoque.php"),
    E("ao_mudar_status_pedido", "Ao mudar status do pedido"),
  ],
  omie: [
    A("consultar_cliente", "Consultar cliente"),
    A("consultar_financeiro", "Consultar financeiro / 2ª via"),
    E("ao_baixar_conta", "Ao baixar/gerar conta"),
  ],
  mautic: [
    A("add_campanha", "Adicionar a campanha"),
    A("aplicar_tag", "Aplicar tag/segmento"),
    // O Mautic NÃO tem evento de webhook para campanha nem para segmento (a lista
    // nativa cobre contato, formulário, e-mail, página e pontuação). O que existe
    // é a AÇÃO de campanha "Send a webhook". Por isso este gatilho é montado do
    // lado do Mautic, e o guia abaixo é obrigatório — sem ele o cliente ativa o
    // gatilho, não configura nada e fica esperando um evento que nunca vem.
    // O payload deste gatilho é DEFINIDO POR QUEM CONFIGURA a ação no Mautic
    // (a ação "Send a webhook" tem editor de campos com tokens). Então nós
    // PRESCREVEMOS o formato: o guia manda montar exatamente estes campos e o
    // `out` declara os mesmos — assim o seletor de variáveis mostra "Telefone",
    // "E-mail", e ninguém precisa abrir JSON.
    E(
      "ao_entrar_campanha",
      "Ao entrar em campanha ou segmento",
      {
        fields: [
          { path: "telefone", desc: "telefone do contato (DDD+número). USE COMO DESTINO ao ligar/enviar SMS." },
          { path: "nome", desc: "nome do contato" },
          { path: "email", desc: "e-mail do contato" },
          { path: "contato_id", desc: "id do contato no Mautic (para consultar/atualizar depois)" },
          { path: "campanha", desc: "nome da campanha que disparou" },
        ],
        sample: {
          telefone: "51981184103",
          nome: "Maria Silva",
          email: "maria@exemplo.com.br",
          contato_id: "841600",
          campanha: "Clientes novos - boas-vindas",
        },
      },
      {
        title: "Como ligar este gatilho no Mautic",
        intro:
          "O Mautic não avisa sozinho quando um contato entra numa campanha ou segmento — quem avisa é uma ação dentro da campanha. São 4 passos, uma única vez por campanha.",
        steps: [
          "No Mautic, abra Campanhas e crie (ou edite) a campanha que deve disparar este fluxo.",
          "Em Fontes da campanha, escolha o segmento que você quer observar — todo contato que entrar nele entra na campanha.",
          "No fluxo da campanha, adicione uma AÇÃO do tipo “Enviar um webhook” (Send a webhook). Use método POST e cole a URL abaixo.",
          "Ainda na ação, monte os dados exatamente com estes 5 campos (copie e cole os tokens):\n" +
            "telefone = {contactfield=phone}\n" +
            "nome = {contactfield=firstname}\n" +
            "email = {contactfield=email}\n" +
            "contato_id = {contactfield=id}\n" +
            "campanha = nome da campanha (texto livre)\n" +
            "Salve e publique a campanha.",
        ],
        note:
          "Os nomes dos campos importam: é por eles que o fluxo enxerga “Telefone”, “Nome” e “E-mail” no seletor de variáveis, sem você mexer em JSON. Se o contato usar celular em outro campo, troque por {contactfield=mobile}. Preencheu o Token de verificação aqui? Some o mesmo valor no cabeçalho x-webhook-secret da ação, senão a chamada é recusada.",
      }
    ),
  ],
  zendesk: [
    A("abrir_ticket", "Abrir ticket", "POST", "/tickets.json"),
    E("ao_abrir_ticket", "Ao abrir ticket"),
  ],
  iugu: [
    A("consultar_cobranca", "Consultar cobrança", "GET", "/invoices/{{id}}"),
    E("ao_confirmar_pagamento", "Ao confirmar pagamento"),
  ],
  mercadopago: [
    A("consultar_pagamento", "Consultar status de pagamento", "GET", "/v1/payments/{{id}}"),
    E("ao_aprovar_pagamento", "Ao aprovar pagamento"),
  ],
  zenvia: [
    A("enviar_sms", "Enviar SMS", "POST", "/channels/sms/messages"),
    A("enviar_whatsapp", "Enviar WhatsApp", "POST", "/channels/whatsapp/messages"),
    E("ao_receber_mensagem", "Ao receber mensagem"),
  ],
  calendly: [
    A("buscar_horarios", "Buscar horários", "GET", "/event_type_available_times"),
    E("ao_agendar", "Ao agendar reunião"),
    E("ao_cancelar", "Ao cancelar"),
  ],
  airtable: [
    A("criar_registro", "Criar registro", "POST", "/{{baseId}}/{{table}}"),
    A("buscar_registro", "Buscar registro", "GET", "/{{baseId}}/{{table}}"),
  ],
  webhook: [A("enviar_dados", "Enviar dados (HTTP)", "POST", "")],

  attio: [
    A("listar_objetos", "Listar objetos", "GET", "/v2/objects"),
    A("criar_objeto", "Criar objeto", "POST", "/v2/objects", "{\n  \"data\": {\n    \"api_slug\": \"people\",\n    \"singular_noun\": \"Person\",\n    \"plural_noun\": \"People\"\n  }\n}"),
    {
      key: "buscar_objeto", label: "Buscar objeto", kind: "action", method: "GET",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
      ],
      path: d => `/v2/objects/${d.object || ""}`,
    },
    {
      key: "atualizar_objeto", label: "Atualizar objeto", kind: "action", method: "PATCH",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/objects/${d.object || ""}`,
    },
    {
      key: "apagar_objeto", label: "Apagar objeto", kind: "action", method: "DELETE",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
      ],
      path: d => `/v2/objects/${d.object || ""}`,
    },
    {
      key: "listar_visoes_do_objeto", label: "Listar visões do objeto", kind: "action", method: "GET",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
      ],
      path: d => `/v2/objects/${d.object || ""}/views`,
    },
    A("listar_atividades", "Listar atividades", "GET", "/v2/activities"),
    A("criar_atividade", "Criar atividade", "POST", "/v2/activities", "{\n  \"data\": {\n    \"api_slug\": \"site_visits\",\n    \"singular_noun\": \"Site visit\",\n    \"plural_noun\": \"Site visits\",\n    \"extends\": \"interactions\"\n  }\n}"),
    {
      key: "buscar_atividade", label: "Buscar atividade", kind: "action", method: "GET",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
      ],
      path: d => `/v2/activities/${d.activity || ""}`,
    },
    {
      key: "atualizar_atividade", label: "Atualizar atividade", kind: "action", method: "PATCH",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/activities/${d.activity || ""}`,
    },
    {
      key: "apagar_atividade", label: "Apagar atividade", kind: "action", method: "DELETE",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
      ],
      path: d => `/v2/activities/${d.activity || ""}`,
    },
    {
      key: "listar_atributos", label: "Listar atributos", kind: "action", method: "GET",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes`,
    },
    {
      key: "criar_atributo", label: "Criar atributo", kind: "action", method: "POST",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"title\": \"Your Attribute\",\n    \"description\": \"Lorem ipsum\",\n    \"api_slug\": \"my_attribute\",\n    \"type\": \"text\",\n    \"is_required\": true,\n    \"is_unique\": true,\n    \"is_multiselect\": true,\n    \"config\": {}\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes`,
    },
    {
      key: "buscar_atributo", label: "Buscar atributo", kind: "action", method: "GET",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}`,
    },
    {
      key: "atualizar_atributo", label: "Atualizar atributo", kind: "action", method: "PATCH",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}`,
    },
    {
      key: "listar_opcoes_de_selecao", label: "Listar opções de seleção", kind: "action", method: "GET",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}/options`,
    },
    {
      key: "criar_opcao_de_selecao", label: "Criar opção de seleção", kind: "action", method: "POST",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"title\": \"Medium\"\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}/options`,
    },
    {
      key: "atualizar_opcao_de_selecao", label: "Atualizar opção de seleção", kind: "action", method: "PATCH",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
        { name: "option", label: "ID da opção", type: "text", required: true, helperText: "Id da opção de seleção." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}/options/${d.option || ""}`,
    },
    {
      key: "listar_status", label: "Listar status", kind: "action", method: "GET",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}/statuses`,
    },
    {
      key: "criar_status", label: "Criar status", kind: "action", method: "POST",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"title\": \"In Progress\"\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}/statuses`,
    },
    {
      key: "atualizar_status", label: "Atualizar status", kind: "action", method: "PATCH",
      form: [
        { name: "target", label: "Alvo", type: "text", required: true, default: "objects", helperText: "Onde o atributo vive: \"objects\" ou \"lists\"." },
        { name: "identifier", label: "Objeto/Lista", type: "text", required: true, helperText: "Slug ou id do objeto/lista dono do atributo." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
        { name: "status", label: "ID do status", type: "text", required: true, helperText: "Id do status." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/${d.target || ""}/${d.identifier || ""}/attributes/${d.attribute || ""}/statuses/${d.status || ""}`,
    },
    {
      key: "listar_registros_com_filtro_e_ordem", label: "Listar registros (com filtro e ordem)", kind: "action", method: "POST",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/query`,
    },
    {
      key: "criar_registro", label: "Criar registro", kind: "action", method: "POST",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "objects", identifierField: "object", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records`,
    },
    {
      key: "criar_ou_atualizar_registro", label: "Criar ou atualizar registro", kind: "action", method: "PUT",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "objects", identifierField: "object", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records`,
    },
    {
      key: "buscar_registro", label: "Buscar registro", kind: "action", method: "GET",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/${d.record_id || ""}`,
    },
    {
      key: "atualizar_registro_substitui_multiselecao", label: "Atualizar registro (substitui multiseleção)", kind: "action", method: "PUT",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "objects", identifierField: "object", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/${d.record_id || ""}`,
    },
    {
      key: "atualizar_registro_acrescenta_em_multiselecao", label: "Atualizar registro (acrescenta em multiseleção)", kind: "action", method: "PATCH",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "objects", identifierField: "object", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/${d.record_id || ""}`,
    },
    {
      key: "apagar_registro", label: "Apagar registro", kind: "action", method: "DELETE",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/${d.record_id || ""}`,
    },
    {
      key: "mesclar_dois_registros", label: "Mesclar dois registros", kind: "action", method: "POST",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"primary_record_id\": \"891dcbfc-9141-415d-9b2a-2238a6cc012d\",\n    \"secondary_record_id\": \"bf071e1f-6035-429d-b874-d83ea64ea13b\"\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/merge`,
    },
    {
      key: "listar_valores_de_atributo_do_registro", label: "Listar valores de atributo do registro", kind: "action", method: "GET",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/${d.record_id || ""}/attributes/${d.attribute || ""}/values`,
    },
    {
      key: "gravar_valores_de_atributo_do_registro", label: "Gravar valores de atributo do registro", kind: "action", method: "PUT",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"values\": [\n      {\n        \"value\": \"Acme (old name)\",\n        \"active_from\": \"2020-01-01T00:00:00Z\",\n        \"active_until\": \"2021-06-15T09:30:00Z\"\n      },\n      {\n        \"value\": \"Acme Corporation\",\n        \"active_from\": \"2021-06-15T09:30:00Z\",\n        \"active_until\": null\n      }\n    ],\n    \"replace_history\": true\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/${d.record_id || ""}/attributes/${d.attribute || ""}/values`,
    },
    {
      key: "listar_itens_de_lista_do_registro", label: "Listar itens de lista do registro", kind: "action", method: "GET",
      form: [
        { name: "object", label: "Objeto", type: "text", required: true, default: "people", helperText: "Slug do objeto (\"people\", \"companies\", e os que seu workspace tiver) ou o id. Use a ação \"Listar objetos\" para descobrir. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
      ],
      path: d => `/v2/objects/${d.object || ""}/records/${d.record_id || ""}/entries`,
    },
    A("procurar_registros_busca_livre", "Procurar registros (busca livre)", "POST", "/v2/objects/records/search", "{\n  \"query\": \"alan mathis\",\n  \"objects\": [\n    \"people\",\n    \"deals\",\n    \"1b31b79a-ddf9-4d57-a320-884061b2bcff\"\n  ],\n  \"request_as\": \"\"\n}"),
    {
      key: "listar_registros_de_atividade_com_filtro", label: "Listar registros de atividade (com filtro)", kind: "action", method: "POST",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/activities/${d.activity || ""}/records/query`,
    },
    {
      key: "criar_registro_de_atividade", label: "Criar registro de atividade", kind: "action", method: "POST",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "activities", identifierField: "activity", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/activities/${d.activity || ""}/records`,
    },
    {
      key: "criar_ou_atualizar_registro_de_atividade", label: "Criar ou atualizar registro de atividade", kind: "action", method: "PUT",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "activities", identifierField: "activity", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/activities/${d.activity || ""}/records`,
    },
    {
      key: "buscar_registro_de_atividade", label: "Buscar registro de atividade", kind: "action", method: "GET",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
      ],
      path: d => `/v2/activities/${d.activity || ""}/records/${d.record_id || ""}`,
    },
    {
      key: "atualizar_registro_de_atividade_substitui_multiselec", label: "Atualizar registro de atividade (substitui multiseleção)", kind: "action", method: "PUT",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "activities", identifierField: "activity", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/activities/${d.activity || ""}/records/${d.record_id || ""}`,
    },
    {
      key: "atualizar_registro_de_atividade_acrescenta_em_multis", label: "Atualizar registro de atividade (acrescenta em multiseleção)", kind: "action", method: "PATCH",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "activities", identifierField: "activity", valuesKey: "values", default: "{\n  \"data\": {\n    \"values\": {\n      \"name\": \"{{steps.trigger.event.body.nome}}\",\n      \"email_addresses\": [\"{{steps.trigger.event.body.email}}\"],\n      \"phone_numbers\": [\"{{steps.trigger.from}}\"]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/activities/${d.activity || ""}/records/${d.record_id || ""}`,
    },
    {
      key: "apagar_registro_de_atividade", label: "Apagar registro de atividade", kind: "action", method: "DELETE",
      form: [
        { name: "activity", label: "Atividade", type: "text", required: true, helperText: "Slug ou id da atividade. Aceita variável." },
        { name: "record_id", label: "ID do registro", type: "text", required: true, helperText: "Id do registro (UUID). Aceita variável." },
      ],
      path: d => `/v2/activities/${d.activity || ""}/records/${d.record_id || ""}`,
    },
    A("consultar_via_sql", "Consultar via SQL", "POST", "/v2/sql", "{\n  \"sql\": \"SELECT * FROM companies WHERE companies.name = 'Fundstack'\"\n}"),
    A("listar_listas", "Listar listas", "GET", "/v2/lists"),
    A("criar_lista", "Criar lista", "POST", "/v2/lists", "{\n  \"data\": {\n    \"name\": \"Enterprise Sales\",\n    \"api_slug\": \"enterprise_sales\",\n    \"parent_object\": \"people\",\n    \"workspace_access\": \"read-and-write\",\n    \"workspace_member_access\": []\n  }\n}"),
    {
      key: "buscar_lista", label: "Buscar lista", kind: "action", method: "GET",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
      ],
      path: d => `/v2/lists/${d.list || ""}`,
    },
    {
      key: "atualizar_lista", label: "Atualizar lista", kind: "action", method: "PATCH",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/lists/${d.list || ""}`,
    },
    {
      key: "listar_visoes_da_lista", label: "Listar visões da lista", kind: "action", method: "GET",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
      ],
      path: d => `/v2/lists/${d.list || ""}/views`,
    },
    {
      key: "listar_itens_da_lista_com_filtro_e_ordem", label: "Listar itens da lista (com filtro e ordem)", kind: "action", method: "POST",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries/query`,
    },
    {
      key: "criar_item_de_lista_adiciona_registro_a_lista", label: "Criar item de lista (adiciona registro à lista)", kind: "action", method: "POST",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "lists", identifierField: "list", valuesKey: "entry_values", default: "{\n  \"data\": {\n    \"parent_record_id\": \"891dcbfc-9141-415d-9b2a-2238a6cc012d\",\n    \"parent_object\": \"people\",\n    \"entry_values\": {\n      \"41252299-f8c7-4b5e-99c9-4ff8321d2f96\": \"Text value\",\n      \"multiselect_attribute\": [\n        \"Select option 1\",\n        \"Select option 2\"\n      ]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries`,
    },
    {
      key: "criar_ou_atualizar_item_de_lista_pelo_registro_de_or", label: "Criar ou atualizar item de lista pelo registro de origem", kind: "action", method: "PUT",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "lists", identifierField: "list", valuesKey: "entry_values", default: "{\n  \"data\": {\n    \"parent_record_id\": \"891dcbfc-9141-415d-9b2a-2238a6cc012d\",\n    \"parent_object\": \"people\",\n    \"entry_values\": {\n      \"41252299-f8c7-4b5e-99c9-4ff8321d2f96\": \"Text value\",\n      \"multiselect_attribute\": [\n        \"Select option 1\",\n        \"Select option 2\"\n      ]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries`,
    },
    {
      key: "buscar_item_de_lista", label: "Buscar item de lista", kind: "action", method: "GET",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "entry_id", label: "ID do item", type: "text", required: true, helperText: "Id do item da lista (UUID). Aceita variável." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries/${d.entry_id || ""}`,
    },
    {
      key: "atualizar_item_de_lista_substitui_multiselecao", label: "Atualizar item de lista (substitui multiseleção)", kind: "action", method: "PUT",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "entry_id", label: "ID do item", type: "text", required: true, helperText: "Id do item da lista (UUID). Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "lists", identifierField: "list", valuesKey: "entry_values", default: "{\n  \"data\": {\n    \"entry_values\": {\n      \"41252299-f8c7-4b5e-99c9-4ff8321d2f96\": \"Text value\",\n      \"multiselect_attribute\": [\n        \"Select option 1\",\n        \"Select option 2\"\n      ]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries/${d.entry_id || ""}`,
    },
    {
      key: "atualizar_item_de_lista_acrescenta_em_multiselecao", label: "Atualizar item de lista (acrescenta em multiseleção)", kind: "action", method: "PATCH",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "entry_id", label: "ID do item", type: "text", required: true, helperText: "Id do item da lista (UUID). Aceita variável." },
        { name: "body", label: "Campos", type: "attioValues", target: "lists", identifierField: "list", valuesKey: "entry_values", default: "{\n  \"data\": {\n    \"entry_values\": {\n      \"41252299-f8c7-4b5e-99c9-4ff8321d2f96\": \"Text value\",\n      \"multiselect_attribute\": [\n        \"Select option 1\",\n        \"Select option 2\"\n      ]\n    }\n  }\n}", helperText: "Campos do Attio. A lista vem do seu workspace \u2014 atributo criado l\u00e1 aparece aqui. Cada campo conhece o pr\u00f3prio tipo, ent\u00e3o telefone sai em E.164 e op\u00e7\u00e3o de select vem da lista real." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries/${d.entry_id || ""}`,
    },
    {
      key: "apagar_item_de_lista", label: "Apagar item de lista", kind: "action", method: "DELETE",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "entry_id", label: "ID do item", type: "text", required: true, helperText: "Id do item da lista (UUID). Aceita variável." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries/${d.entry_id || ""}`,
    },
    {
      key: "listar_valores_de_atributo_do_item", label: "Listar valores de atributo do item", kind: "action", method: "GET",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "entry_id", label: "ID do item", type: "text", required: true, helperText: "Id do item da lista (UUID). Aceita variável." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries/${d.entry_id || ""}/attributes/${d.attribute || ""}/values`,
    },
    {
      key: "gravar_valores_de_atributo_do_item", label: "Gravar valores de atributo do item", kind: "action", method: "PUT",
      form: [
        { name: "list", label: "Lista", type: "text", required: true, helperText: "Slug ou id da lista. Use a ação \"Listar listas\" para descobrir. Aceita variável." },
        { name: "entry_id", label: "ID do item", type: "text", required: true, helperText: "Id do item da lista (UUID). Aceita variável." },
        { name: "attribute", label: "Atributo", type: "text", required: true, helperText: "Slug ou id do atributo. Aceita variável." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"values\": [\n      {\n        \"value\": \"Acme (old name)\",\n        \"active_from\": \"2020-01-01T00:00:00Z\",\n        \"active_until\": \"2021-06-15T09:30:00Z\"\n      },\n      {\n        \"value\": \"Acme Corporation\",\n        \"active_from\": \"2021-06-15T09:30:00Z\",\n        \"active_until\": null\n      }\n    ],\n    \"replace_history\": true\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/lists/${d.list || ""}/entries/${d.entry_id || ""}/attributes/${d.attribute || ""}/values`,
    },
    A("listar_membros_do_workspace", "Listar membros do workspace", "GET", "/v2/workspace_members"),
    {
      key: "buscar_membro_do_workspace", label: "Buscar membro do workspace", kind: "action", method: "GET",
      form: [
        { name: "workspace_member_id", label: "ID do membro", type: "text", required: true, helperText: "Id do membro do workspace (UUID)." },
      ],
      path: d => `/v2/workspace_members/${d.workspace_member_id || ""}`,
    },
    A("listar_notas", "Listar notas", "GET", "/v2/notes"),
    A("criar_nota", "Criar nota", "POST", "/v2/notes", "{\n  \"data\": {\n    \"parent_object\": \"people\",\n    \"parent_record_id\": \"891dcbfc-9141-415d-9b2a-2238a6cc012d\",\n    \"title\": \"Initial Prospecting Call Summary\",\n    \"format\": \"markdown\",\n    \"content\": \"# Meeting Recap: Q4 Planning\\n\\n**Date:** 2023-10-26\\n**Attendees:** Alex, Jamie, Casey\\n\\n## Key Discussion Points\\n\\n- Reviewed Q3 performance metrics.\\n- Brainstormed key initiatives for Q4.\\n- Discussed budget allocation for ==Project Phoenix==.\\n\\n## Action Items\\n\\n1. Alex to finalize Q4 roadmap by EOD Friday.\\n2. Jamie to schedule follow-up with [Marketing Team](https://app.attio.com/teams/marketing).\\n3. Casey to draft initial budget for ~~Project Chimera~~ (now deferred).\\n\\n*Next steps: Review draft roadmap next week.*\"\n  }\n}"),
    {
      key: "buscar_nota", label: "Buscar nota", kind: "action", method: "GET",
      form: [
        { name: "note_id", label: "ID da nota", type: "text", required: true, helperText: "Id da nota (UUID)." },
      ],
      path: d => `/v2/notes/${d.note_id || ""}`,
    },
    {
      key: "atualizar_nota", label: "Atualizar nota", kind: "action", method: "PATCH",
      form: [
        { name: "note_id", label: "ID da nota", type: "text", required: true, helperText: "Id da nota (UUID)." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/notes/${d.note_id || ""}`,
    },
    {
      key: "apagar_nota", label: "Apagar nota", kind: "action", method: "DELETE",
      form: [
        { name: "note_id", label: "ID da nota", type: "text", required: true, helperText: "Id da nota (UUID)." },
      ],
      path: d => `/v2/notes/${d.note_id || ""}`,
    },
    A("listar_tarefas", "Listar tarefas", "GET", "/v2/tasks"),
    A("criar_tarefa", "Criar tarefa", "POST", "/v2/tasks", "{\n  \"data\": {\n    \"content\": \"Follow up on current software solutions\",\n    \"format\": \"plaintext\",\n    \"deadline_at\": \"2023-01-01T15:00:00.000000000Z\",\n    \"is_completed\": false,\n    \"linked_records\": [],\n    \"assignees\": []\n  }\n}"),
    {
      key: "buscar_tarefa", label: "Buscar tarefa", kind: "action", method: "GET",
      form: [
        { name: "task_id", label: "ID da tarefa", type: "text", required: true, helperText: "Id da tarefa (UUID)." },
      ],
      path: d => `/v2/tasks/${d.task_id || ""}`,
    },
    {
      key: "atualizar_tarefa", label: "Atualizar tarefa", kind: "action", method: "PATCH",
      form: [
        { name: "task_id", label: "ID da tarefa", type: "text", required: true, helperText: "Id da tarefa (UUID)." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/tasks/${d.task_id || ""}`,
    },
    {
      key: "apagar_tarefa", label: "Apagar tarefa", kind: "action", method: "DELETE",
      form: [
        { name: "task_id", label: "ID da tarefa", type: "text", required: true, helperText: "Id da tarefa (UUID)." },
      ],
      path: d => `/v2/tasks/${d.task_id || ""}`,
    },
    A("listar_conversas", "Listar conversas", "GET", "/v2/threads"),
    {
      key: "buscar_conversa_e_seus_comentarios", label: "Buscar conversa e seus comentários", kind: "action", method: "GET",
      form: [
        { name: "thread_id", label: "ID da conversa", type: "text", required: true, helperText: "Id da conversa (UUID)." },
      ],
      path: d => `/v2/threads/${d.thread_id || ""}`,
    },
    A("criar_comentario", "Criar comentário", "POST", "/v2/comments", "{\n  \"data\": \"\"\n}"),
    {
      key: "buscar_comentario", label: "Buscar comentário", kind: "action", method: "GET",
      form: [
        { name: "comment_id", label: "ID do comentário", type: "text", required: true, helperText: "Id do comentário (UUID)." },
      ],
      path: d => `/v2/comments/${d.comment_id || ""}`,
    },
    {
      key: "apagar_comentario", label: "Apagar comentário", kind: "action", method: "DELETE",
      form: [
        { name: "comment_id", label: "ID do comentário", type: "text", required: true, helperText: "Id do comentário (UUID)." },
      ],
      path: d => `/v2/comments/${d.comment_id || ""}`,
    },
    A("listar_e_mails", "Listar e-mails", "GET", "/v2/emails"),
    A("adicionar_e_mails_a_lista_de_descadastro", "Adicionar e-mails à lista de descadastro", "POST", "/v2/sequences/unsubscribed_emails", "{\n  \"data\": {\n    \"email_addresses\": []\n  }\n}"),
    A("listar_reunioes", "Listar reuniões", "GET", "/v2/meetings"),
    A("criar_reuniao", "Criar reunião", "POST", "/v2/meetings", "{\n  \"data\": {\n    \"title\": \"Onboarding Session\",\n    \"description\": \"Getting you up to speed with the platform and answering any questions you have.\",\n    \"start\": \"\",\n    \"end\": \"\",\n    \"is_all_day\": false,\n    \"participants\": []\n  }\n}"),
    {
      key: "buscar_reuniao", label: "Buscar reunião", kind: "action", method: "GET",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}`,
    },
    {
      key: "atualizar_reuniao_substitui_registros_ligados", label: "Atualizar reunião (substitui registros ligados)", kind: "action", method: "PUT",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"linked_records\": []\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}`,
    },
    {
      key: "atualizar_reuniao_acrescenta_registros_ligados", label: "Atualizar reunião (acrescenta registros ligados)", kind: "action", method: "PATCH",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {\n    \"linked_records\": []\n  }\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}`,
    },
    {
      key: "apagar_reuniao", label: "Apagar reunião", kind: "action", method: "DELETE",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}`,
    },
    {
      key: "listar_gravacoes_de_chamada", label: "Listar gravações de chamada", kind: "action", method: "GET",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}/call_recordings`,
    },
    {
      key: "criar_gravacao_de_chamada", label: "Criar gravação de chamada", kind: "action", method: "POST",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}/call_recordings`,
    },
    {
      key: "buscar_gravacao_de_chamada", label: "Buscar gravação de chamada", kind: "action", method: "GET",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
        { name: "call_recording_id", label: "ID da gravação", type: "text", required: true, helperText: "Id da gravação (UUID)." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}/call_recordings/${d.call_recording_id || ""}`,
    },
    {
      key: "apagar_gravacao_de_chamada", label: "Apagar gravação de chamada", kind: "action", method: "DELETE",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
        { name: "call_recording_id", label: "ID da gravação", type: "text", required: true, helperText: "Id da gravação (UUID)." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}/call_recordings/${d.call_recording_id || ""}`,
    },
    {
      key: "buscar_transcricao_da_gravacao_descontinuado_na_atti", label: "Buscar transcrição da gravação (descontinuado na Attio)", kind: "action", method: "GET",
      form: [
        { name: "meeting_id", label: "ID da reunião", type: "text", required: true, helperText: "Id da reunião (UUID)." },
        { name: "call_recording_id", label: "ID da gravação", type: "text", required: true, helperText: "Id da gravação (UUID)." },
      ],
      path: d => `/v2/meetings/${d.meeting_id || ""}/call_recordings/${d.call_recording_id || ""}/transcript`,
    },
    A("listar_arquivos", "Listar arquivos", "GET", "/v2/files"),
    A("criar_pasta", "Criar pasta", "POST", "/v2/files"),
    A("enviar_arquivo", "Enviar arquivo", "POST", "/v2/files/upload", "{\n  \"file\": \"\",\n  \"object\": \"people\",\n  \"record_id\": \"bf071e1f-6035-429d-b874-d83ea64ea13b\"\n}"),
    {
      key: "buscar_arquivo", label: "Buscar arquivo", kind: "action", method: "GET",
      form: [
        { name: "file_id", label: "ID do arquivo", type: "text", required: true, helperText: "Id do arquivo (UUID)." },
      ],
      path: d => `/v2/files/${d.file_id || ""}`,
    },
    {
      key: "apagar_arquivo", label: "Apagar arquivo", kind: "action", method: "DELETE",
      form: [
        { name: "file_id", label: "ID do arquivo", type: "text", required: true, helperText: "Id do arquivo (UUID)." },
      ],
      path: d => `/v2/files/${d.file_id || ""}`,
    },
    {
      key: "baixar_arquivo", label: "Baixar arquivo", kind: "action", method: "GET",
      form: [
        { name: "file_id", label: "ID do arquivo", type: "text", required: true, helperText: "Id do arquivo (UUID)." },
      ],
      path: d => `/v2/files/${d.file_id || ""}/download`,
    },
    A("listar_webhooks", "Listar webhooks", "GET", "/v2/webhooks"),
    A("criar_webhook", "Criar webhook", "POST", "/v2/webhooks", "{\n  \"data\": {\n    \"target_url\": \"https://example.com/webhook\",\n    \"subscriptions\": []\n  }\n}"),
    {
      key: "buscar_webhook", label: "Buscar webhook", kind: "action", method: "GET",
      form: [
        { name: "webhook_id", label: "ID do webhook", type: "text", required: true, helperText: "Id do webhook (UUID)." },
      ],
      path: d => `/v2/webhooks/${d.webhook_id || ""}`,
    },
    {
      key: "atualizar_webhook", label: "Atualizar webhook", kind: "action", method: "PATCH",
      form: [
        { name: "webhook_id", label: "ID do webhook", type: "text", required: true, helperText: "Id do webhook (UUID)." },
        { name: "body", label: "Dados (JSON)", type: "textarea", default: "{\n  \"data\": {}\n}", helperText: "Corpo enviado ao Attio. O Attio envolve tudo em \"data\". Aceita variáveis." },
      ],
      path: d => `/v2/webhooks/${d.webhook_id || ""}`,
    },
    {
      key: "apagar_webhook", label: "Apagar webhook", kind: "action", method: "DELETE",
      form: [
        { name: "webhook_id", label: "ID do webhook", type: "text", required: true, helperText: "Id do webhook (UUID)." },
      ],
      path: d => `/v2/webhooks/${d.webhook_id || ""}`,
    },
    A("identificar_o_token_conta_e_permissoes", "Identificar o token (conta e permissões)", "GET", "/v2/self"),
    E("activity_created", "Ao ser criada uma atividade", ATTIO_EV_ATIVIDADE, ATTIO_SETUP),
    E("activity_updated", "Ao ser atualizada uma atividade", ATTIO_EV_ATIVIDADE, ATTIO_SETUP),
    E("activity_deleted", "Ao ser apagada uma atividade", ATTIO_EV_ATIVIDADE, ATTIO_SETUP),
    E("activity_attribute_created", "Ao ser criado um atributo de atividade", ATTIO_EV_ATIVIDADE_ATRIBUTO, ATTIO_SETUP),
    E("activity_attribute_updated", "Ao ser atualizado um atributo de atividade", ATTIO_EV_ATIVIDADE_ATRIBUTO, ATTIO_SETUP),
    E("activity_record_created", "Ao ser criado um registro de atividade", ATTIO_EV_ATIVIDADE_REGISTRO, ATTIO_SETUP),
    E("activity_record_deleted", "Ao ser apagado um registro de atividade", ATTIO_EV_ATIVIDADE_REGISTRO, ATTIO_SETUP),
    E("call_recording_created", "Ao ser criada uma gravação de chamada", ATTIO_EV_MEETING_ID_GRAVACAO, ATTIO_SETUP),
    E("comment_created", "Ao ser criado um comentário", ATTIO_EV_COMENTARIO, ATTIO_SETUP),
    E("comment_resolved", "Ao ser resolvido um comentário", ATTIO_EV_COMENTARIO, ATTIO_SETUP),
    E("comment_unresolved", "Ao ser reaberto um comentário", ATTIO_EV_COMENTARIO, ATTIO_SETUP),
    E("comment_deleted", "Ao ser apagado um comentário", ATTIO_EV_COMENTARIO, ATTIO_SETUP),
    E("list_created", "Ao ser criada uma lista", ATTIO_EV_LISTA, ATTIO_SETUP),
    E("list_updated", "Ao ser atualizada uma lista", ATTIO_EV_LISTA, ATTIO_SETUP),
    E("list_deleted", "Ao ser apagada uma lista", ATTIO_EV_LISTA, ATTIO_SETUP),
    E("list_attribute_created", "Ao ser criado um atributo de lista", ATTIO_EV_LISTA_ATRIBUTO, ATTIO_SETUP),
    E("list_attribute_updated", "Ao ser atualizado um atributo de lista", ATTIO_EV_LISTA_ATRIBUTO, ATTIO_SETUP),
    E("list_entry_created", "Ao ser criado um item de lista", ATTIO_EV_LISTA_ITEM, ATTIO_SETUP),
    E("list_entry_updated", "Ao ser atualizado um item de lista", ATTIO_EV_LISTA_ITEM_ATRIBUTO, ATTIO_SETUP),
    E("list_entry_deleted", "Ao ser apagado um item de lista", ATTIO_EV_LISTA_ITEM, ATTIO_SETUP),
    E("object_attribute_created", "Ao ser criado um atributo de objeto", ATTIO_EV_OBJECT_ID_ATRIBUTO, ATTIO_SETUP),
    E("object_attribute_updated", "Ao ser atualizado um atributo de objeto", ATTIO_EV_OBJECT_ID_ATRIBUTO, ATTIO_SETUP),
    E("note_created", "Ao ser criada uma nota", ATTIO_EV_NOTA, ATTIO_SETUP),
    E("note_content_updated", "Ao ser atualizado conteúdo da nota", ATTIO_EV_NOTA, ATTIO_SETUP),
    E("note_updated", "Ao ser atualizada uma nota", ATTIO_EV_NOTA, ATTIO_SETUP),
    E("note_deleted", "Ao ser apagada uma nota", ATTIO_EV_NOTA, ATTIO_SETUP),
    E("record_created", "Ao ser criado um registro", ATTIO_EV_OBJECT_ID_REGISTRO, ATTIO_SETUP),
    E("record_merged", "Ao ser mesclado um registro", ATTIO_EV_OBJECT_ID_REGISTRO_2, ATTIO_SETUP),
    E("record_updated", "Ao ser atualizado um registro", ATTIO_EV_OBJECT_ID_REGISTRO_ATRIBUTO, ATTIO_SETUP),
    E("record_deleted", "Ao ser apagado um registro", ATTIO_EV_OBJECT_ID_REGISTRO, ATTIO_SETUP),
    E("task_created", "Ao ser criada uma tarefa", ATTIO_EV_TAREFA, ATTIO_SETUP),
    E("task_updated", "Ao ser atualizada uma tarefa", ATTIO_EV_TAREFA, ATTIO_SETUP),
    E("task_deleted", "Ao ser apagada uma tarefa", ATTIO_EV_TAREFA, ATTIO_SETUP),
    E("workspace_member_created", "Ao ser criado um membro do workspace", ATTIO_EV_MEMBRO, ATTIO_SETUP),
  ],
};
